import { Injectable } from '@angular/core';
import { filterStoreCatalog, sortStoreCatalog, STORE_CATALOG } from '../data/store-catalog.fixture';
import {
  AccessibleMap,
  ComposedRail,
  ComposerSignals,
  ContentKind,
  DeviceBudget,
  LandingBlock,
  MAP_GENRE_LABEL,
  RailIntent,
  StoreDensity,
  StoreFilters,
  StoreItem,
} from '../models/store-rail.model';

const ADVENTURE_TYPES: ContentKind[] = [
  'Adventure',
  'One Shot Encounter',
  'Side Quest',
  'Mini Campaign / Adventure Arc',
  'Campaign',
];

export function utcDateKey(date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

export function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) >>> 0;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function deviceBudget(width: number): DeviceBudget {
  if (width <= 767) {
    return { maxRails: 4, densities: [2, 3], allowTwoRows: false };
  }
  if (width <= 1200) {
    return { maxRails: 5, densities: [2, 3, 4, 5], allowTwoRows: false };
  }
  return { maxRails: 7, densities: [2, 3, 4, 5], allowTwoRows: true };
}

export function seeAllParams(filters: StoreFilters): Record<string, string> {
  const q: Record<string, string> = { sort: filters.sort };
  if (filters.type) q['type'] = filters.type;
  if (filters.types?.length) q['types'] = filters.types.join(',');
  if (filters.world) q['world'] = filters.world;
  if (filters.biome) q['biome'] = filters.biome;
  if (filters.gamesystem) q['gamesystem'] = filters.gamesystem;
  if (filters.freeOnly) q['free'] = '1';
  if (filters.priceMax != null) q['priceMax'] = String(filters.priceMax);
  if (filters.playtimeMax != null) q['playtimeMax'] = String(filters.playtimeMax);
  if (filters.minRating != null) q['minRating'] = String(filters.minRating);
  if (filters.author) q['author'] = filters.author;
  return q;
}

const MAP_CATEGORY_TEMPLATES: Array<{
  id: string;
  subtitle: (mapName: string) => string;
  filters: StoreFilters;
  createType?: ContentKind;
  baseScore: number;
}> = [
  {
    id: 'new-establishments',
    subtitle: (mapName) => `New inns, shops and halls on ${mapName}.`,
    filters: { type: 'Establishment', sort: 'newest' },
    createType: 'Establishment',
    baseScore: 8,
  },
  {
    id: 'latest',
    subtitle: (mapName) => `Fresh modules pinned to ${mapName}.`,
    filters: { sort: 'newest' },
    baseScore: 7,
  },
  {
    id: 'top-rated',
    subtitle: (mapName) => `What tables keep running on ${mapName}.`,
    filters: { sort: 'rating', minRating: 4.5 },
    baseScore: 6,
  },
];

export function categoryIntentPool(signals: ComposerSignals): RailIntent[] {
  const bought = signals.purchasedTypes[0];

  const intents: RailIntent[] = [
    {
      id: 'latest-modules',
      kind: 'category',
      titleLead: 'Latest',
      titleGold: 'modules',
      subtitle: 'Fresh adventures, establishments and encounter packs from the store.',
      filters: { sort: 'newest' },
      baseScore: 8,
    },
    {
      id: 'new-establishments',
      kind: 'category',
      titleLead: 'New',
      titleGold: 'establishments',
      subtitle: 'Inns, shops and halls you can drop on a map tonight.',
      filters: { type: 'Establishment', sort: 'newest' },
      createType: 'Establishment',
      baseScore: 7,
    },
    {
      id: 'highest-rated-establishments',
      kind: 'category',
      titleLead: 'Highest rated',
      titleGold: 'establishments',
      subtitle: 'What tables keep coming back to.',
      filters: { type: 'Establishment', sort: 'rating', minRating: 4.5 },
      createType: 'Establishment',
      baseScore: 6,
    },
    {
      id: 'new-campaigns',
      kind: 'category',
      titleLead: 'New',
      titleGold: 'campaigns',
      subtitle: 'Longer arcs ready to run.',
      filters: { type: 'Campaign', sort: 'newest' },
      createType: 'Campaign',
      baseScore: 6,
    },
    {
      id: 'playable-session',
      kind: 'category',
      titleLead: 'Playable this',
      titleGold: 'session',
      subtitle: 'Short tables — two or three hours.',
      filters: { sort: 'newest', playtimeMax: 3 },
      createType: 'One Shot Encounter',
      baseScore: 5,
    },
    {
      id: 'coastal',
      kind: 'category',
      titleLead: 'Coastal',
      titleGold: 'adventures',
      subtitle: 'Tide, salt and drowned stone.',
      filters: { types: ADVENTURE_TYPES, biome: 'coast', sort: 'newest' },
      createType: 'Adventure',
      baseScore: 5,
    },
    {
      id: 'dungeon',
      kind: 'category',
      titleLead: 'Dungeon',
      titleGold: 'delves',
      subtitle: 'Sealed vaults and wet stone.',
      filters: { types: ADVENTURE_TYPES, biome: 'dungeon', sort: 'rating' },
      createType: 'Adventure',
      baseScore: 4,
    },
    {
      id: 'free-to-run',
      kind: 'category',
      titleLead: 'Free to',
      titleGold: 'run',
      subtitle: 'No coin at the door.',
      filters: { sort: 'newest', freeOnly: true },
      baseScore: 5,
    },
    {
      id: 'top-rated',
      kind: 'category',
      titleLead: 'Top',
      titleGold: 'rated',
      subtitle: 'Highest rated across the store.',
      filters: { sort: 'rating', minRating: 4.5 },
      baseScore: 6,
    },
    {
      id: 'dnd-5e',
      kind: 'category',
      titleLead: 'D&D 5e',
      titleGold: 'modules',
      subtitle: 'Built for fifth edition tables.',
      filters: { gamesystem: 'D&D 5e', sort: 'popular' },
      baseScore: 5,
    },
    {
      id: 'pathfinder',
      kind: 'category',
      titleLead: 'Pathfinder 2e',
      titleGold: 'modules',
      subtitle: 'Built for Pathfinder tables.',
      filters: { gamesystem: 'Pathfinder 2e', sort: 'popular' },
      createType: 'Adventure',
      baseScore: 4,
    },
  ];

  if (bought) {
    intents.push({
      id: `because-bought-${bought}`,
      kind: 'category',
      titleLead: 'Because you bought',
      titleGold: bought.toLowerCase() + 's',
      subtitle: 'More in the vein of what you already run.',
      filters: { type: bought, sort: 'rating' },
      createType: bought,
      baseScore: 9,
    });
  }

  return intents;
}

export function mapIntentPool(signals: ComposerSignals): RailIntent[] {
  return (signals.accessibleMaps || []).flatMap((map) =>
    MAP_CATEGORY_TEMPLATES.map((template) => mapScopedIntent(map, template)),
  );
}

function mapScopedIntent(
  map: AccessibleMap,
  template: (typeof MAP_CATEGORY_TEMPLATES)[number],
): RailIntent {
  return {
    id: `map:${map.key}:${template.id}`,
    kind: 'map',
    titleLead: 'On',
    titleGold: map.name,
    subtitle: template.subtitle(map.name),
    filters: { ...template.filters, world: map.key },
    createType: template.createType,
    baseScore: template.baseScore,
    genre: map.genre,
    mapKey: map.key,
  };
}

export function intentPool(signals: ComposerSignals): RailIntent[] {
  return [...categoryIntentPool(signals), ...mapIntentPool(signals)];
}

function scoreIntent(intent: RailIntent, signals: ComposerSignals, rng: () => number): number {
  let score = intent.baseScore + rng() * 1.4;
  const plan = (signals.planName || '').toLowerCase();

  if (plan === 'adventurer' || !plan) {
    if (intent.filters.freeOnly || intent.filters.playtimeMax) score += 2;
  }
  if (plan === 'creator' || plan === 'architect') {
    if (intent.filters.type === 'Establishment') score += 2;
  }

  if (intent.kind === 'map' && intent.mapKey === signals.activeWorldKey) {
    score += 2.5;
  }

  if (signals.purchasedTypes.length) {
    if (intent.filters.type && signals.purchasedTypes.includes(intent.filters.type)) {
      score += 2;
    }
  }

  for (const facet of signals.searchFacets || []) {
    if (facet.type && (intent.filters.type === facet.type || intent.filters.types?.includes(facet.type))) {
      score += 1.5;
    }
    if (facet.biome && intent.filters.biome === facet.biome) score += 1.5;
    if (facet.gamesystem && intent.filters.gamesystem === facet.gamesystem) score += 1.2;
  }

  return score;
}

function assignDensity(
  previous: StoreDensity | null,
  budget: DeviceBudget,
  rng: () => number,
  used: Set<StoreDensity>,
  preferred?: StoreDensity,
): { density: StoreDensity; rows: 1 | 2 } {
  if (preferred && budget.densities.includes(preferred) && preferred !== previous) {
    used.add(preferred);
    return { density: preferred, rows: 1 };
  }
  const notPrev = (d: StoreDensity) => d !== previous;
  const unused = budget.densities.filter((d) => notPrev(d) && !used.has(d));
  const adjacentOk = budget.densities.filter(notPrev);
  const pool = unused.length ? unused : adjacentOk.length ? adjacentOk : budget.densities;
  const density = pool[Math.floor(rng() * pool.length)] ?? pool[0];
  used.add(density);
  let rows: 1 | 2 = 1;
  if (budget.allowTwoRows && density === 4 && rng() > 0.55) {
    rows = 2;
  }
  return { density, rows };
}

function fillRail(
  intent: RailIntent,
  density: StoreDensity,
  rows: 1 | 2,
  catalog: StoreItem[],
): ComposedRail | null {
  const matched = sortStoreCatalog(filterStoreCatalog(catalog, intent.filters), intent.filters.sort);
  if (!matched.length) return null;

  const slots = density * rows;
  const items = matched.slice(0, slots);
  const createCount = Math.max(0, slots - items.length);

  return {
    id: intent.id,
    kind: intent.kind,
    titleLead: intent.titleLead,
    titleGold: intent.titleGold,
    subtitle: intent.subtitle,
    density,
    rows,
    filters: intent.filters,
    seeAllParams: seeAllParams(intent.filters),
    items,
    createPlaceholders: Array.from({ length: createCount }, (_, i) => i),
    createType: intent.createType,
    genre: intent.genre,
    genreLabel: intent.genre ? MAP_GENRE_LABEL[intent.genre] : undefined,
    mapKey: intent.mapKey,
  };
}

@Injectable({ providedIn: 'root' })
export class LandingRailComposerService {
  compose(
    signals: ComposerSignals,
    width: number,
    catalog: StoreItem[] = STORE_CATALOG,
    now = new Date(),
  ): LandingBlock[] {
    const budget = deviceBudget(width);
    const seedKey = `${signals.userId ?? 'anon'}|${utcDateKey(now)}`;
    const rng = mulberry32(hashString(seedKey));

    const scored = intentPool(signals)
      .map((intent) => ({ intent, score: scoreIntent(intent, signals, rng) }))
      .sort((a, b) => b.score - a.score);

    const rails: ComposedRail[] = [];
    let previousDensity: StoreDensity | null = null;
    const usedDensities = new Set<StoreDensity>();
    const usedMaps = new Set<string>();
    const maxMapRails = budget.maxRails >= 6 ? 2 : 1;

    for (const { intent } of scored) {
      if (rails.length >= budget.maxRails) break;
      if (rails.some((r) => r.id === intent.id)) continue;
      if (intent.kind === 'map') {
        if (usedMaps.size >= maxMapRails) continue;
        if (intent.mapKey && usedMaps.has(intent.mapKey)) continue;
      }
      const { density, rows } = assignDensity(
        previousDensity,
        budget,
        rng,
        usedDensities,
        intent.density,
      );
      const rail = fillRail(intent, density, rows, catalog);
      if (!rail) continue;
      rails.push(rail);
      previousDensity = density;
      if (intent.kind === 'map' && intent.mapKey) usedMaps.add(intent.mapKey);
    }

    const ashmorrowIntent = scored.find((s) => s.intent.kind === 'map' && s.intent.mapKey === 'ashmorrow');
    if (ashmorrowIntent && !usedMaps.has('ashmorrow')) {
      const { density, rows } = assignDensity(previousDensity, budget, rng, usedDensities);
      const rail = fillRail(ashmorrowIntent.intent, density, rows, catalog);
      if (rail) {
        rails.unshift(rail);
        if (rails.length > budget.maxRails) rails.pop();
      }
    }

    const blocks: LandingBlock[] = rails.map((rail) => ({ kind: 'rail' as const, rail }));
    if (blocks.length >= 3) {
      blocks.splice(2, 0, { kind: 'featured-map' });
    } else if (blocks.length) {
      blocks.push({ kind: 'featured-map' });
    }
    return blocks;
  }

  composeDensityGallery(catalog: StoreItem[] = STORE_CATALOG): ComposedRail[] {
    const notes: Record<StoreDensity, string> = {
      5: 'Title, then 2h · 3–5 · Lvl. System pill on the image. Hover: pitch and author.',
      4: 'Line 1 title, line 2 hours/players/levels, line 3 game system, line 4 stars. System pill on the image. Hover: larger text and author.',
      3: 'Line 1 title, line 2 hours/players/levels, line 3 game system, line 4 stars. System pill on the image. Hover: larger text and author.',
      2: 'Line 1 title, line 2 hours/players/levels, line 3 game system, line 4 stars and author. System pill on the image. Hover: smaller description.',
    };
    const densities: StoreDensity[] = [5, 4, 3, 2];
    const rails = densities.map((density) =>
      fillRail(
        {
          id: 'preview-' + density,
          kind: 'category',
          titleLead: String(density),
          titleGold: 'across',
          subtitle: notes[density],
          filters: { sort: 'newest' },
          baseScore: 0,
        },
        density,
        1,
        catalog,
      ),
    );
    const twoRow = fillRail(
      {
        id: 'preview-4-rows',
        kind: 'category',
        titleLead: '4 across',
        titleGold: 'two rows',
        subtitle: 'Same 4-wide card as above, stacked on two rows when the composer allows it.',
        filters: { sort: 'newest' },
        createType: 'Establishment',
        baseScore: 0,
      },
      4,
      2,
      catalog,
    );
    return [...rails, twoRow].filter((rail): rail is ComposedRail => !!rail);
  }
}
